import { EntityHeader } from '@pesto/features/src/EntityProfile/components/EntityHeader';
import { useUrlTabs } from '@pesto/hooks';
import { Card, LoadingSpinner, type OverflowTabItem, OverflowTabs } from '@pesto/ui';
import { useParams } from 'react-router-dom';

import { useGetUserByIdQuery } from '../../__generated__/graphql/api';
import { NotFound } from '../NotFound';
import { UserCardsTab } from './components/UserCardsTab';
import { UserDetailsTab } from './components/UserDetailsTab/UserDetailsTab';
import { UserDevicesTab } from './components/UserDevicesTab/UserDevicesTab';
import { UserLoansTab } from './components/UserLoansTab';
import { toUserDetail } from './toUserDetail';
import { buildUserHeaderConfig } from './userHeaderConfig';

export const UserDetailsPage = () => {
  const { id } = useParams<{ id: string }>();

  const { data, loading } = useGetUserByIdQuery({
    variables: { userId: id ?? '' },
    skip: !id,
    notifyOnNetworkStatusChange: true,
  });

  const userResult = data?.listUsers?.users?.find((item) => item?.id === id) ?? null;
  const user = userResult ? toUserDetail(userResult) : null;

  const primary: OverflowTabItem[] = [
    {
      label: 'Details',
      value: 'details',
      component: user ? <UserDetailsTab user={user} /> : null,
    },
    {
      label: 'Loans',
      value: 'loans',
      component: user ? <UserLoansTab loans={user.loans} /> : null,
      count: user?.loans.length,
    },
    {
      label: 'Cards',
      value: 'cards',
      component: user ? <UserCardsTab cards={user.cards} /> : null,
      count: user?.cards.length,
    },
    {
      label: 'Devices',
      value: 'devices',
      component: id ? <UserDevicesTab userId={id} /> : null,
    },
  ];

  const validTabs = primary.map((item) => item.value);

  const { currentTab, setCurrentTab } = useUrlTabs({
    defaultTab: 'details',
    validTabs,
    basePath: `/users/${id ?? ''}`,
  });

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <NotFound />;
  }

  return (
    <main className="flex flex-1 flex-col gap-4 p-4 md:p-8">
      <Card>
        <EntityHeader config={buildUserHeaderConfig(user)} />
      </Card>
      <OverflowTabs primary={primary} value={currentTab} onValueChange={setCurrentTab} />
    </main>
  );
};
